import * as THREE from "three";
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Colors } from "utils/imports.utils";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";

export const TRANSFORM_BOX_RENDER_ORDER_OFFSET = 0.5;
const OUTLINE_WIDTH_PX = 3;
const HANDLE_SIZE_PX = 10;
const HANDLE_BORDER_PX = 2;
const HANDLE_RADIUS_PX = 2;
const HANDLE_FILL_COLOR = 0xffffff;

const traceRoundedSquare = (path: THREE.Path, size: number, radius: number) => {
    const half = size / 2;
    const r = Math.min(radius, half);
    path.moveTo(-half + r, -half);
    path.lineTo(half - r, -half);
    path.quadraticCurveTo(half, -half, half, -half + r);
    path.lineTo(half, half - r);
    path.quadraticCurveTo(half, half, half - r, half);
    path.lineTo(-half + r, half);
    path.quadraticCurveTo(-half, half, -half, half - r);
    path.lineTo(-half, -half + r);
    path.quadraticCurveTo(-half, -half, -half + r, -half);
};

const createRoundedSquareGeometry = (size: number, radius: number) => {
    const shape = new THREE.Shape();
    traceRoundedSquare(shape, size, radius);
    return new THREE.ShapeGeometry(shape, 6);
};

// A ring (outer square with the inner square cut out), so the border never overlaps the white
// fill and no draw-order between the two can paint the corner solid lime.
const createRoundedRingGeometry = (outerSize: number, innerSize: number, outerRadius: number, innerRadius: number) => {
    const shape = new THREE.Shape();
    traceRoundedSquare(shape, outerSize, outerRadius);
    const hole = new THREE.Path();
    traceRoundedSquare(hole, innerSize, innerRadius);
    shape.holes.push(hole);
    return new THREE.ShapeGeometry(shape, 6);
};

// Everything the box owns on the GPU: edge lines and the handles' meshes (fill + border).
const disposeTransformBox = (box: THREE.Object3D) => {
    box.traverse((child) => {
        if (child instanceof Line2 || child instanceof THREE.Mesh) {
            if (child.geometry) child.geometry.dispose();
            [child.material].flat().forEach((material) => material.dispose());
        }
    });
};

const createCornerHandle = (corner: string, position: THREE.Vector3, zoom: number) => {
    const size = HANDLE_SIZE_PX / zoom;
    const borderSize = (HANDLE_SIZE_PX + HANDLE_BORDER_PX * 2) / zoom;
    const radius = HANDLE_RADIUS_PX / zoom;

    const border = new THREE.Mesh(
        createRoundedRingGeometry(borderSize, size, radius + HANDLE_BORDER_PX / zoom, radius),
        new THREE.MeshBasicMaterial({ color: Colors.transformBoxColor, depthTest: false, transparent: true }),
    );
    border.renderOrder = 1000;
    border.raycast = () => {};

    const handle = new THREE.Mesh(
        createRoundedSquareGeometry(size, radius),
        new THREE.MeshBasicMaterial({ color: HANDLE_FILL_COLOR, depthTest: false, transparent: true }),
    );
    handle.position.copy(position);
    handle.position.z = 0.01;
    handle.renderOrder = 1001;
    handle.raycast = () => {};
    handle.userData.isTransformBox = true;
    handle.userData.handleCorner = corner;
    handle.add(border);
    return handle;
};

export const renderTransformBox = (workspace: WorkspaceContext, object: THREE.Object3D) => {
    const { rendererRef, sceneRef, cameraRef, transformBoxRef } = workspace.refs;
    if (!sceneRef.current || !cameraRef.current) return null;
   
    // Clean up previous transform box (same as before)
    if (object.userData.transformBox) {
        object.userData.transformBox.removeFromParent();
        disposeTransformBox(object.userData.transformBox);
    }

    // Calculate bounding box from the object's unrotated extents. Box3.setFromObject()
    // measures the world-space AXIS-ALIGNED bounds, which balloons once the object is
    // rotated (it encloses the rotated shape instead of describing it) — and this box's
    // own rotation is then set to object.rotation below, so rotating the input first would
    // double-apply the rotation. Zeroing rotation.z for the measurement keeps size accurate
    // for any current rotation.
    const currentRotationZ = object.rotation.z;
    object.rotation.z = 0;
    object.updateMatrixWorld(true);
    const boundingBox = new THREE.Box3().setFromObject(object);
    const size = boundingBox.getSize(new THREE.Vector3());
    object.rotation.z = currentRotationZ;
    object.updateMatrixWorld(true);
    
    // For 2D, we only care about X and Y dimensions
    const halfWidth = Math.max(0.1, size.x * 0.5);
    const halfHeight = Math.max(0.1, size.y * 0.5);
    
    // Define only 4 corners for a 2D rectangle (on the same Z plane)
    const corners = [
        new THREE.Vector3(-halfWidth, -halfHeight, 0), // Bottom-Left
        new THREE.Vector3(halfWidth, -halfHeight, 0),  // Bottom-Right  
        new THREE.Vector3(halfWidth, halfHeight, 0),   // Top-Right
        new THREE.Vector3(-halfWidth, halfHeight, 0),  // Top-Left
    ];

    // Define edges for a simple rectangle (4 lines)
    const edges = [
        [0, 1], // Bottom edge: Bottom-Left → Bottom-Right
        [1, 2], // Right edge: Bottom-Right → Top-Right  
        [2, 3], // Top edge: Top-Right → Top-Left
        [3, 0], // Left edge: Top-Left → Bottom-Left
    ];

    // Store corner points for interaction
    const cornerPoints = {
        topLeft: corners[3],     // Top-Left
        topRight: corners[2],    // Top-Right
        bottomLeft: corners[0],  // Bottom-Left
        bottomRight: corners[1], // Bottom-Right
    };

    object.userData.cornerPoints = cornerPoints;

    // Create the transform box group
    const transformBoxGroup = new THREE.Group();

    // Create material
    const material = new LineMaterial({
        color: Colors.transformBoxColor,
        linewidth: OUTLINE_WIDTH_PX,
        transparent: true,
        resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    });

    // Create the 4 edges
    edges.forEach(([start, end], index) => {
        const geometry = new LineGeometry();
        const points = [corners[start], corners[end]].flatMap(p => [p.x, p.y, p.z]);
        geometry.setPositions(points);

        const line = new Line2(geometry, material);
        // Transparent materials draw after opaque ones, then by renderOrder: keep the outline
        // below the handles (renderOrder 1000+) so the corners sit in front of it.
        line.renderOrder = 999;
        line.raycast = () => {};
        line.userData.isTransformBox = true;

        // Tag edge types for 2D
        const edgeTypes = ["bottom", "right", "top", "left"];
        line.userData.edgeType = edgeTypes[index];
        
        // Primary axis for 2D
        line.userData.primaryAxis = (index % 2 === 0) ? "x" : "y";

        transformBoxGroup.add(line);
    });
   
    Object.entries(cornerPoints).forEach(([corner, point]) => {
        transformBoxGroup.add(createCornerHandle(corner, point, cameraRef.current?.zoom ?? 1));
    });

    transformBoxGroup.position.copy(object.position);
    transformBoxGroup.rotation.copy(object.rotation);

    transformBoxGroup.userData.detectCorner = (mousePosition: THREE.Vector2) => {
        if (!cameraRef.current || !rendererRef.current) return null;

        const toScreen = (point: THREE.Vector3) => {
            if (!cameraRef.current || !rendererRef.current) return null;

            const rotatedPoint = point.clone();
            rotatedPoint.applyEuler(transformBoxGroup.rotation);
            // rotatedPoint.multiply(transformBoxGroup.scale);

            const worldPoint = rotatedPoint.add(transformBoxGroup.position);
        
            const vector = worldPoint.clone().project(cameraRef.current);
            return new THREE.Vector2(
                (vector.x + 1) * rendererRef.current.domElement.clientWidth / 2,
                (-vector.y + 1) * rendererRef.current.domElement.clientHeight / 2
            );
        };

        const screenCorners = {
            topLeft: toScreen(cornerPoints.topLeft),
            topRight: toScreen(cornerPoints.topRight),
            bottomLeft: toScreen(cornerPoints.bottomLeft),
            bottomRight: toScreen(cornerPoints.bottomRight),
        };

        const detectionRadius = 15;

        for (const [corner, position] of Object.entries(screenCorners)) {
            const distance = position?.distanceTo(mousePosition) ?? Infinity;
            if (distance < detectionRadius) {
                return corner;
            }
        }

        return null;
    };

    // The renderer runs with sortObjects = false (draw order = traversal order, with only the
    // target-image group's children sorted by renderOrder each frame), so the box has to be a
    // sibling of its object carrying the object's renderOrder to be stacked with it. Parked at
    // the scene root it would always paint over every object. The +0.5 keeps it just in front of
    // its own object and behind any object with a higher renderOrder.
    transformBoxGroup.renderOrder = (object.renderOrder ?? 0) + TRANSFORM_BOX_RENDER_ORDER_OFFSET;
    (object.parent ?? sceneRef.current).add(transformBoxGroup);
    transformBoxRef.current = transformBoxGroup;
    object.userData.transformBox = transformBoxGroup;
    transformBoxGroup.userData.associatedObjectID = object.userData.id;

    if (rendererRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
};

export const cleanupTransformBox = (workspace: WorkspaceContext, object) => {
    const { rendererRef, sceneRef, cameraRef, transformBoxRef } = workspace.refs;
    if (object && object.userData.transformBox) {
        // Remove from scene
        object.userData.transformBox.removeFromParent();
        
        // Dispose of geometries and materials
        disposeTransformBox(object.userData.transformBox);
        
        // Clear references
        object.userData.transformBox = null;
        transformBoxRef.current = null;
        
        // Re-render the scene
        if (rendererRef.current && cameraRef.current && sceneRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
        
        // Reset cursor to default
        document.body.style.cursor = 'default';
    }
};
